/**
 * Agents Management Page
 * 
 * Main page for managing agents with features:
 * - Agent list with pagination
 * - Create/Edit/Delete agents
 * - Status management
 * 
 * Uses StandardPage template for consistent layout
 */

import { FC, useCallback, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCrudPage } from '@/hooks';
import { agentService } from '@/services';
import StandardPage from '@/components/Common/StandardPage';
import DataTable from '@/components/DataTable/DataTable';
import { createAgentColumns } from '@/components/DataTable/agentColumns';
import { AgentFiltersComponent } from '@/components/AgentFilters';
import Modal from '@/components/Common/Modal';
import ConfirmDialog from '@/components/Common/ConfirmDialog';
import AgentForm from '@/components/Forms/AgentForm';
import type { AgentFormData } from '@/components/Forms/AgentForm';
import type { AgentListItem, AgentFilters as AgentFiltersType } from '@/types';

const AgentsPage: FC = () => {
  const navigate = useNavigate();
  const [agentToDelete, setAgentToDelete] = useState<AgentListItem | null>(null);
  const [filters, setFilters] = useState<AgentFiltersType>({});

  /**
   * Use CRUD page hook
   */
  const {
    data: agents,
    total,
    isLoading,
    isError,
    error,
    pagination,
    setPagination,
    isModalOpen,
    selectedItem: selectedAgent,
    mutations: { update: updateMutation, delete: deleteMutation },
    refetch,
    handleOpenEdit,
    handleCancelModal,
  } = useCrudPage({
    queryKey: 'agents',
    service: {
      getList: (paginationFilters: any) =>
        agentService.getAgents(
          {
            page: paginationFilters.page,
            size: paginationFilters.size,
            sort: paginationFilters.sortBy && paginationFilters.sortOrder ? `${paginationFilters.sortBy},${paginationFilters.sortOrder}` : undefined,
          },
          filters
        ),
      create: agentService.createAgent,
      update: agentService.updateAgent,
      delete: agentService.deleteAgent,
    },
    initialPageSize: 10,
    successMessages: {
      create: {
        title: 'Agente criado!',
        message: 'O agente foi criado com sucesso.',
      },
      update: {
        title: 'Agente atualizado!',
        message: 'As alterações foram salvas com sucesso.',
      },
      delete: {
        title: 'Agente deletado!',
        message: 'O agente foi removido com sucesso.',
      },
    },
  });

  /**
   * Handle filter changes
   */
  const handleFiltersChange = useCallback(
    (newFilters: AgentFiltersType) => {
      setFilters(newFilters);
    },
    []
  );

  /**
   * Handle clear filters
   */
  const handleClearFilters = useCallback(() => {
    setFilters({});
  }, []);

  /**
   * Refetch when filters change
   */
  useEffect(() => {
    refetch();
  }, [filters, refetch]);

  /**
   * Handle view agent detail
   */
  const handleView = useCallback(
    (agent: AgentListItem) => {
      navigate(`/agents/${agent.ref}`);
    },
    [navigate]
  );

  /**
   * Handle delete agent (open confirmation)
   */
  const handleOpenDelete = useCallback((agent: AgentListItem) => {
    setAgentToDelete(agent);
  }, []);

  /**
   * Handle confirm delete
   */
  const handleConfirmDelete = useCallback(async () => {
    if (agentToDelete) {
      await deleteMutation.mutateAsync(agentToDelete.ref);
      setAgentToDelete(null);
    }
  }, [agentToDelete, deleteMutation]);

  /**
   * Handle cancel delete
   */
  const handleCancelDelete = useCallback(() => {
    setAgentToDelete(null);
  }, []);

  /**
   * Create columns with actions memoized
   */
  const columns = useMemo(
    () =>
      createAgentColumns({
        onEdit: handleOpenEdit,
        onView: handleView,
        onDelete: handleOpenDelete,
      }),
    [handleOpenEdit, handleView, handleOpenDelete]
  );

  /**
   * Handle form submit
   */
  const handleFormSubmit = useCallback(
    async (data: AgentFormData) => {
      if (selectedAgent) {
        await updateMutation.mutateAsync({
          id: selectedAgent.ref, // Use ref instead of id
          data,
        });
      }
    },
    [selectedAgent, updateMutation]
  );

  return (
    <>
      <StandardPage
        header={{
          title: 'Gerenciamento de Agentes',
          subtitle: 'Gerencie os agentes do sistema',
        }}
        isLoading={isLoading}
        isError={isError}
        error={error}
        onRefetch={refetch}
      >
        {/* Agent Filters */}
        <AgentFiltersComponent
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          isLoading={isLoading}
        />

        <DataTable
          data={agents}
          columns={columns}
          isLoading={false}
          enableSorting={true}
          enablePagination={true}
          emptyMessage="Nenhum agente encontrado"
          showSkeleton={true}
          pageSizeOptions={[5, 10, 20, 50, 100]}
          defaultPageSize={10}
          showPageSizeSelector={true}
          totalRows={total}
          pagination={pagination}
          onPaginationChange={setPagination}
        />
      </StandardPage>

      {/* Agent Modal (Edit only) */}
      <Modal
        open={isModalOpen}
        onClose={handleCancelModal}
        title="Editar Agente"
        description="Atualize as informações do agente"
        size="md"
      >
        <AgentForm
          mode="edit"
          initialValues={selectedAgent || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleCancelModal}
          isLoading={updateMutation.isPending}
        />
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!agentToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Deletar Agente"
        message={
          agentToDelete
            ? `Tem certeza que deseja deletar o agente "${agentToDelete.name}"? Esta ação não pode ser desfeita.`
            : ''
        }
        confirmText="Deletar"
        cancelText="Cancelar"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};

export default AgentsPage;
